/*
  Resolves a product's purchasable variant from Shopify, by handle.

  The GoPuff products API also returns a `shopify_variant_id`, but it comes from a
  per-product Redis cache written by catalog sync. That cache holds an id Shopify
  owns and can recreate: when a sync rebuilds a product's variants the cached id
  points at a deleted variant, and add-to-cart fails with
  `422 Cart Error / Cannot find variant`. Asking Shopify directly cannot go stale.

  No variant selection is needed here. BevMo products carry one variant per scannable
  barcode for POS, but those are not published to the Online Store channel, so the
  Storefront API returns only the online one.

  Lookups are coalesced: handles requested in the same tick go out as one aliased
  query, so a 50-tile collection render costs a single request. Results are cached for
  the life of the page. Anything that fails resolves to null so callers keep whatever
  they already had rather than losing add-to-cart entirely.
*/
window.onlineVariant = (() => {
  const API_VERSION = `2024-07`;
  const MAX_PER_REQUEST = 50;

  // A hung request would otherwise never deliver, leaving every tile in the batch
  // pending — quick-add disabled for the rest of the page view. Abandoning on a timeout
  // returns callers to the cached id, which is the documented failure behaviour.
  const TIMEOUT_MS = 5000;

  const resolved = new Map();
  const waiting = new Map();
  const inFlight = new Set();
  let scheduled = false;

  const variantIdFromGid = (gid) => {
    const id = Number.parseInt(String(gid).split(`/`).pop(), 10);
    return Number.isInteger(id) ? id : null;
  };

  const deliver = (handle, variantId) => {
    inFlight.delete(handle);

    const callbacks = waiting.get(handle) || [];
    waiting.delete(handle);
    callbacks.forEach((callback) => callback(variantId));
  };

  // A definitive answer from Shopify: either a variant id, or null for a product it does
  // not have. Cached for the rest of the page.
  const settle = (handle, variantId) => {
    resolved.set(handle, variantId);
    deliver(handle, variantId);
  };

  // No answer — HTTP, network or GraphQL failure. Callers get null so they keep whatever
  // they already had, but nothing is cached, so a later render batch retries. Caching a
  // failure would strand every tile for that handle on the stale id until a full reload.
  const abandon = (handles) => {
    handles.forEach((handle) => deliver(handle, null));
  };

  const fetchBatch = async (handles) => {
    const token = window.gopuffShopify?.settings?.storefront_api_token;
    if (!token) {
      abandon(handles);
      return;
    }

    // Aliased `product(handle:)` lookups. `products(query:)` is a full-text search and
    // silently ignores a `handle:` term, so it cannot be used to fetch known handles.
    const params = handles.map((_, index) => `$h${index}: String!`).join(`, `);
    const fields = handles
      .map((_, index) => `p${index}: product(handle: $h${index}) { variants(first: 1) { nodes { id } } }`)
      .join(`\n`);
    const variables = {};
    handles.forEach((handle, index) => { variables[`h${index}`] = handle; });

    try {
      const response = await fetch(`https://${Shopify.shop}/api/${API_VERSION}/graphql`, {
        method: `POST`,
        headers: { 'Content-Type': `application/json`, 'X-Shopify-Storefront-Access-Token': token },
        body: JSON.stringify({ query: `query OnlineVariants(${params}) {\n${fields}\n}`, variables }),
        signal: AbortSignal.timeout(TIMEOUT_MS),
      });
      if (!response.ok) {
        console.error(`online-variant: lookup failed`, response.status);
        abandon(handles);
        return;
      }

      const body = await response.json();
      if (!body || !body.data) {
        console.error(`online-variant: lookup returned no data`, body?.errors);
        abandon(handles);
        return;
      }

      handles.forEach((handle, index) => {
        const alias = `p${index}`;

        // An alias missing from `data` means this entry errored; only an alias that is
        // present is an answer. Present-and-null is Shopify saying it has no such product.
        if (!(alias in body.data)) {
          abandon([handle]);
          return;
        }

        const gid = body.data[alias]?.variants?.nodes?.[0]?.id;
        settle(handle, gid ? variantIdFromGid(gid) : null);
      });
    } catch (error) {
      console.error(`online-variant: lookup failed`, error);
      abandon(handles);
    }
  };

  const flush = () => {
    scheduled = false;

    const handles = [...waiting.keys()].filter((handle) => !inFlight.has(handle));
    handles.forEach((handle) => inFlight.add(handle));

    for (let start = 0; start < handles.length; start += MAX_PER_REQUEST) {
      fetchBatch(handles.slice(start, start + MAX_PER_REQUEST));
    }
  };

  /** The online variant id for a handle, or null when there is none to use. */
  const resolve = (handle) => {
    if (!handle) { return Promise.resolve(null); }
    if (resolved.has(handle)) { return Promise.resolve(resolved.get(handle)); }

    return new Promise((callback) => {
      if (!waiting.has(handle)) { waiting.set(handle, []); }
      waiting.get(handle).push(callback);

      if (!scheduled) {
        scheduled = true;
        setTimeout(flush, 0);
      }
    });
  };

  /*
    The already-known answer for a handle, or `undefined` when one has not been fetched.

    Lets a caller distinguish "answer in hand" from "have to wait", so it can apply a cached
    id synchronously instead of holding its UI for a promise that resolves immediately.
  */
  const peek = (handle) => resolved.get(handle);

  return { resolve, peek };
})();
